import {
    styled, Button, Box,
    IconButton,
    InputAdornment,
    TextField,
    Snackbar,
    Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
    useCallback, useEffect, useState,
    useTransition,
} from 'react';
import debounce from 'lodash/debounce';

import { ForcedRankingSearchResults } from 'src/components/ForcedRankingPage/ForcedRankingSearchResults';
import { ForcedRankingSearchResultsSkeleton } from 'src/components/ForcedRankingPage/ForcedRankingSearchResultsSkeleton';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import {
    Add,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryForcedRankingStrategies } from 'src/hooks/useQueryForcedRankingStrategies';
import { CreateNewStrategyModal } from 'src/components/common/DesignConceptForcedRanking/CreateNewStrategyModal';
import { useLocation, useNavigate } from 'react-router-dom';

// Minimal component to handle the suspense boundary
const QueryBoundary = ({ searchTerm }: { searchTerm: string }): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const { data } = useQueryForcedRankingStrategies(accessToken, {
        searchTerm,
        offset: 0,
        limit: 1000,
    });

    return <ForcedRankingSearchResults results={data || []} searchTerm={searchTerm} />;
};

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(8)}`,
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(8),
}));

const SearchContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        background: theme.palette.common.white,
    },
}));

export const ForcedRankingSearchPage = (): JSX.Element => {
    const [searchInputValue, setSearchInputValue] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [isPending, startTransition] = useTransition();
    const [openCreateStrategyPop, setOpenCreateStrategyPop] = useState<boolean>(false);
    const navigate = useNavigate();

    type LocationState = {
        deleted?: boolean;
    };
    const location = useLocation();
    const state = location.state as LocationState | undefined;
    const showSuccess = state?.deleted ?? false;
    const [showDeletionSuccessSnackbar, setShowDeletionSuccessSnackbar] = useState(showSuccess);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetSearchTerm = useCallback(
        debounce((term: string) => {
            startTransition(() => {
                setDebouncedSearchTerm(term);
            });
        }, 300),
        [],
    );

    useEffect(() => {
        debouncedSetSearchTerm(searchInputValue);
        return () => {
            debouncedSetSearchTerm.cancel();
        };
    }, [searchInputValue, debouncedSetSearchTerm]);

    useEffect(() => {
        if (showSuccess) {
            setShowDeletionSuccessSnackbar(true);
            navigate(location.pathname, { replace: true });
        }
    }, [showSuccess, navigate, location.pathname]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchInputValue(e.target.value);
    };

    const renderSearchResults = (): JSX.Element => {
        if (isPending) {
            return <ForcedRankingSearchResultsSkeleton />;
        }
        return (
            <QueryBoundary searchTerm={debouncedSearchTerm} />
        );
    };

    const handleOpenCreateStrategyModal = (): void => {
        setOpenCreateStrategyPop(true);
    };

    const handleCloseCreateStrategyModal = (): void => {
        setOpenCreateStrategyPop(false);
    };

    return (
        <>
            <TitledAppBar title="Design Concept Forced Ranking Search" />
            <StyledContainer>
                <Box sx={{
                    width: '100%',
                    justifyContent: 'flex-end',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
                >
                    <Button
                        startIcon={<Add />}
                        variant="contained"
                        onClick={handleOpenCreateStrategyModal}
                    >
                        Create New Strategy
                    </Button>
                </Box>
                <SearchContainer>
                    <StyledTextField
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton edge="end" type="submit">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Search strategies by name..."
                        size="small"
                        sx={{
                            maxWidth: '600px',
                            width: '100%',
                        }}
                        value={searchInputValue}
                        onChange={handleSearchChange}
                    />
                </SearchContainer>
                {renderSearchResults()}
            </StyledContainer>
            <CreateNewStrategyModal
                open={openCreateStrategyPop}
                onClose={handleCloseCreateStrategyModal}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={showDeletionSuccessSnackbar}
                onClose={((): void => setShowDeletionSuccessSnackbar(false))}
            >
                <Alert severity="success" variant="filled" onClose={((): void => setShowDeletionSuccessSnackbar(false))}>
                    Strategy deleted successfully
                </Alert>
            </Snackbar>
        </>
    );
};
